import React, { useState, useEffect } from "react";
import axios from "axios";
import CustomerDetails from "../3_Months_Warranty_Details/CustomerDetails";
import CustomerSchemeTable from "./CustomerSchemeTable";
import Modal from "./Modal";
import LoadingScreen from "../../Admin/Loading_Screen/LoadingScreen";
import API_BASE_URL from "../../ApiConfig/ApiConfig";
import Search from "../../../Assets/Icon search.svg";
import Cookies from 'js-cookie';
import * as XLSX from 'xlsx';

const RegisteredProduct = () => {
  const [CustomerScheme, setCustomerScheme] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(null);
  const [selectedCustomerRecords, setSelectedCustomerRecords] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSchemeFilter, setSelectedSchemeFilter] = useState("All");
  const [schemeNames, setSchemeNames] = useState([]);
  const [selectedSchemeId, setSelectedSchemeId] = useState(null);
  const [schemeCounts, setSchemeCounts] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const token = Cookies.get('admin');

  // Fetch CustomerScheme data
  const fetchCustomerScheme = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/Scheme/fetch`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });
      setCustomerScheme(response.data);

      // Extract unique scheme names
      const uniqueSchemes = [...new Set(response.data.map(item => item.schemeName))];
      setSchemeNames(uniqueSchemes);

      // Calculate scheme counts
      const counts = {};
      response.data.forEach(item => {
        counts[item.schemeName] = (counts[item.schemeName] || 0) + 1;
      });
      setSchemeCounts(counts);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching CustomerScheme:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerScheme();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      const fetchCustomerDetails = async () => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/users/customer/${selectedCustomerId}`,
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
              },
            }
          );
          setSelectedCustomerDetails(response.data);
        } catch (error) {
          console.error("Error fetching customer details:", error);
        }
      };
      const fetchCustomerRecords = async () => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/Scheme/customerid/${selectedCustomerId}`,
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : "",
              },
            }
          );

          // Filter records based on schemeId
          const filteredRecords = response.data.filter(
            (record) => record.schemeId === selectedSchemeId
          );
          setSelectedCustomerRecords(filteredRecords);
        } catch (error) {
          console.error("Error fetching customer records:", error);
        }
      };

      fetchCustomerDetails();
      fetchCustomerRecords();
    }
  }, [selectedCustomerId, selectedSchemeId, token]);

  const handleView = (customerId, schemeId) => {
    setSelectedCustomerId(customerId);
    setSelectedSchemeId(schemeId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onViewInvoice = (invoicePath) => {
    window.open(
      `${API_BASE_URL}${invoicePath}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Filter schemes based on search term and selected scheme
  const filteredSchemes = CustomerScheme.filter((scheme) => {
    const customerIdMatch = scheme.customerId
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const customerNameMatch = scheme.customerName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const schemeMatch = selectedSchemeFilter === "All" ||
      scheme.schemeName === selectedSchemeFilter;

    return (customerIdMatch || customerNameMatch) && schemeMatch;
  });

  // Paginate filtered schemes
  const totalPages = Math.ceil(filteredSchemes.length / itemsPerPage);
  const currentPageData = filteredSchemes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const exportToExcel = async () => {
    try {
      // First, fetch all customer details
      const customerDetailsPromises = filteredSchemes.map(scheme =>
        axios.get(`${API_BASE_URL}/api/users/customer/${scheme.customerId}`, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
        })
      );

      const customerDetailsResponses = await Promise.all(customerDetailsPromises);

      const dataToExport = filteredSchemes.map((scheme, index) => {
        const customerDetails = customerDetailsResponses[index].data;
        return {
          "Customer Name": scheme.customerName,
          "Customer ID": scheme.customerId,
          "Scheme Name": scheme.schemeName,
          "Phone": customerDetails.phone || 'N/A',
          "Email": customerDetails.email || 'N/A',
        };
      });

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Registered Schemes");
      XLSX.writeFile(wb, "Registered_Schemes.xlsx");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      // Fallback to basic export if detailed fetch fails
      const basicDataToExport = filteredSchemes.map(scheme => ({
        "Customer Name": scheme.customerName,
        "Customer ID": scheme.customerId,
        "Scheme Name": scheme.schemeName,
        "Registration Date": formatDate(scheme.createdAt)
      }));
      const ws = XLSX.utils.json_to_sheet(basicDataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Registered Schemes");
      XLSX.writeFile(wb, "Registered_Schemes_Basic.xlsx");
    }
  };

  return (
    <div className="p-6 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-4 sm:mb-0">Manage Schemes</h2>

        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-2/3 justify-end">
          {/* Scheme Filter Dropdown */}
          <div className="relative w-full sm:w-48">
            <select
              value={selectedSchemeFilter}
              onChange={(e) => setSelectedSchemeFilter(e.target.value)}
              className="p-2 pr-8 rounded-lg w-full shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 appearance-none"
            >
              <option value="All">All Schemes ({CustomerScheme.length})</option>
              {schemeNames.map((scheme, index) => (
                <option key={index} value={scheme}>
                  {scheme} ({schemeCounts[scheme] || 0})
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <i className="fas fa-chevron-down text-gray-400"></i>
            </div>
          </div>

          {/* Search Input */}
          <div className="flex-shrink-0 w-full sm:w-1/3 relative">
            <input
              type="text"
              placeholder="Search by Customer ID/Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="p-2 pl-10 pr-3 rounded-lg w-full shadow-sm outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300"
            />
            <img
              src={Search}
              alt="Search Icon"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6"
            />
          </div>

          {/* Export Button */}
          <button
            onClick={exportToExcel}
            className="bg-[#006cb5]  text-white px-4 py-2 rounded-lg flex items-center justify-center"
          >
            <i className="fas fa-file-excel mr-2"></i>
            Export
          </button>
        </div>
      </div>

      <div className="container mx-auto">
        {currentPageData.length > 0 ? (
          <>
            {/* Desktop Headings */}
            <div className="hidden sm:grid grid-cols-12 gap-4 font-semibold text-[#1C50A3] px-4 py-2">
              <div className="col-span-3">Customer Name</div>
              <div className="col-span-2">Customer ID</div>
              <div className="col-span-3">Scheme Name</div>
              <div className="col-span-2">Scheme ID</div>
              <div className="col-span-2 text-right">Action</div>
            </div>

            {/* Data Rows */}
            {currentPageData.map((scheme, index) => (
              <div
                key={`${scheme._id}-${index}`}
                className={`bg-white rounded-xl p-4 mb-4 shadow-md transition-transform duration-300 ease-in-out transform group hover:shadow-lg hover:scale-[1.02] hover:cursor-pointer ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                  }`}
              >
                {/* For Desktop */}
                <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3 font-semibold text-[#292D32] group-hover:text-blue-600 flex items-center gap-2">
                    <div className="w-7 h-7 bg-[#1C50A3] text-white rounded-full flex items-center justify-center font-bold">
                      {scheme.customerName.charAt(0).toUpperCase()}
                    </div>
                    {scheme.customerName}
                  </div>
                  <div className="col-span-2 text-gray-600">{scheme.customerId}</div>
                  <div className="col-span-3 text-gray-600">{scheme.schemeName}</div>
                  <div className="col-span-2 text-gray-600">{scheme.schemeId}</div>
                  <div className="col-span-2">
                    <div className="flex justify-end"> {/* 👈 move text-right here */}
                     
                        <button
                        onClick={() => handleView(scheme.customerId, scheme.schemeId)}
                    className="text-[#1C50A3] bg-[#E8F1FF] rounded-lg px-4 py-1 flex items-center space-x-2 hover:bg-[#D0E0FF] transition-colors"
                  >
                    <i className="fas fa-eye text-sm"></i>
                    <span className="font-semibold">View</span>
                  </button>
                    </div>
                  </div>

                </div>

                {/* For Mobile */}
                <div className="sm:hidden flex flex-col gap-2">
                  <div className="flex justify-between">
                    <p className="font-semibold text-[#292D32]">Customer</p>
                    <p>{scheme.customerName}</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="font-semibold text-[#292D32]">Customer ID</p>
                    <p>{scheme.customerId}</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="font-semibold text-[#292D32]">Scheme Name</p>
                    <p>{scheme.schemeName}</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="font-semibold text-[#292D32]">Scheme ID</p>
                    <p>{scheme.schemeId}</p>
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => handleView(scheme.customerId, scheme.schemeId)}
                      className="text-[#1C50A3] bg-[#E8F1FF] rounded-lg px-4 py-1 flex items-center space-x-2 hover:bg-[#D0E0FF] transition-colors"
                    >
                      <i className="fas fa-eye text-sm"></i>
                      <span className="font-semibold">View</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="py-6 text-center text-gray-500">
            No records found matching your criteria
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredSchemes.length > itemsPerPage && (
        <div className="flex justify-center items-center mt-6 space-x-3">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className="px-4 py-2 mx-1 rounded-md disabled:opacity-50 text-gray-600 text-sm font-semibold flex items-center hover:bg-gray-100"
          >
            <i className="fas fa-chevron-left mr-2"></i> Previous
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => handlePageChange(i + 1)}
              className={`px-3 py-1 mx-1 rounded-md text-sm font-semibold ${currentPage === i + 1
                  ? "bg-[#1C50A3] text-white"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 mx-1 rounded-md disabled:opacity-50 text-gray-600 text-sm font-semibold flex items-center hover:bg-gray-100"
          >
            Next <i className="fas fa-chevron-right ml-2"></i>
          </button>
        </div>
      )}

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedCustomerDetails && (
          <>
            <CustomerDetails details={selectedCustomerDetails} />
            <CustomerSchemeTable
              records={selectedCustomerRecords}
              onViewInvoice={onViewInvoice}
            />
          </>
        )}
      </Modal>

      {loading && <LoadingScreen />}
    </div>
  );
};

export default RegisteredProduct;